import MaterialTable from '@material-table/core';
import { LibraryAdd } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { Chip, TableCell, TableRow } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import { AppConfig } from 'AppConfig';
import axios from 'axios';
import { confirmDialog } from 'common/ComfirmationDialog';
import * as Constant from 'common/Constant';
import * as UserTypes from 'features/user/userTypes';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import * as AppActionTypes from 'store/actions/appstate';
import { store } from 'store/store';
import { v4 as uuid } from 'uuid';
import * as Action from './action/action';
import RoleManagementDialog from './components/roleManagementDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.5)
    }
  }
}));

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black
  },
  body: {
    fontSize: 14
  }
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover
    }
  }
}))(TableRow);

const RoleManagementTable = () => {
  const tableRef = useRef();
  const classes = useStyles();

  const [pageSize, setPageSize] = useState(Constant.DEFAULT_SELECT_PAGE_SIZE);
  const [creationOpen, setCreationOpen] = useState(false);
  const [cloneData, setCloneData] = useState({});
  // Retrieve user email from Redux state
  const userEmail = useSelector((state) => state.user.email);

  const roleList = useSelector((state) => state.adminRoleManagement.roleList);
  const permissionList = useSelector(
    (state) => state.adminRoleManagement.permissionList
  );
  const shouldRefresh = useSelector(
    (state) => state.adminRoleManagement.shouldRefresh
  );

  const refresh = () => {
    tableRef.current && tableRef.current.onQueryChange({ page: 0 });
  };
  useEffect(() => {
    refresh();
  }, [shouldRefresh]);

  useEffect(() => {
    store.dispatch({
      type: UserTypes.APP_SWITCHED,
      payload: AppConfig.HEADER_MENU_TYPE.NONE
    });
    store.dispatch(Action.fetchRoleList());
    store.dispatch(Action.fetchPermissionList());
  }, []);

  useEffect(() => {
      // Send role management action to Clarity
      if (window.clarity && userEmail) {
        window.clarity('set', 'roleManagementAction', userEmail.toLowerCase());
      }
  }, [userEmail]);

  const handleSingleDelete = (roleId, permissionId) => {
    confirmDialog(
      () =>
        store.dispatch(Action.deleteSingleRolePermission(roleId, permissionId)),
      'Do you really want to remove this permission under this role?'
    );
  };

  const handleDelete = (roleId) => {
    confirmDialog(
      () => store.dispatch(Action.deleteRolePermissionComb(roleId)),
      'Do you really want to delete this role?'
    );
  };

  const handleCreationOpen = (data) => {
    setCloneData(data);
    setCreationOpen(true);
  };

  const handleCreationClose = (data) => {
    //convert data to backend expected object
    if (data && data.code && data.description) {
      let dto = {
        roleId: data.id ? data.id : null,
        code: data.code,
        description: data.description,
        permissionIds: Array.isArray(data.permissions)
          ? data.permissions.map((a) => a.id)
          : [],
        applicationCode: data.applicationCode ?? null
      };
      store.dispatch(Action.saveRolePermissionComb(dto));
    }

    setCreationOpen(false);
  };

  return (
    <>
      <RoleManagementDialog
        open={creationOpen}
        onClose={handleCreationClose}
        target={cloneData}
        roleList={roleList}
        permissionList={permissionList}
      />
      <MaterialTable
        title=""
        style={{ width: '100%' }}
        tableRef={tableRef}
        columns={[
          {
            title: 'Role Code',
            field: 'code',
            editable: 'never',
            width: '15%'
          },
          {
            title: 'Description',
            field: 'description',
            editable: 'never',
            width: '15%'
          },
          {
            title: 'Permissions',
            field: 'permissions',
            render: (rowData) => (
              <div className={classes.root}>
                {rowData.permissions.map((data) => (
                  <Chip
                    key={'key-' + uuid()}
                    label={data.code}
                    onDelete={
                      !rowData.isDefault
                        ? () => {
                            handleSingleDelete(rowData.id, data.id);
                          }
                        : null
                    }
                  />
                ))}
              </div>
            ),
            sorting: false
          }
        ]}
        data={(query) =>
          new Promise((resolve, reject) => {
            axios
              .post(AppConfig.IAM_BACKEND_URL + `/role/search`, {
                page: query.page,
                size: query.pageSize,
                direction: query.orderDirection
                  ? query.orderDirection.toUpperCase()
                  : Constant.DIRECTION_ASC,
                sortProperty: query.orderBy ? query.orderBy.field : 'id',
                searchTerm: query.search
              })
              .then((m) => {
                let data = m.data;
                resolve({
                  data: data.content,
                  page: data.number,
                  size: data.size,
                  totalCount: data.totalElements
                });
              })
              .catch((err) => {
                resolve({
                  data: [],
                  page: 0,
                  totalCount: 0
                });
                store.dispatch({
                  type: AppActionTypes.APPSTATE_SET_APP_ERROR,
                  payload: 'Could not retrieve the chart.'
                });
              });
          })
        }
        actions={[
          {
            icon: () => <LibraryAdd />,
            tooltip: 'Clone',
            onClick: (event, rowData) => {
              handleCreationOpen({ ...rowData, id: null });
            }
          },
          (data) => ({
            icon: () => <EditIcon />,
            tooltip: 'Edit',
            onClick: (event, rowData) => {
              handleCreationOpen(rowData);
            }
          }),
          (data) => ({
            icon: () => <DeleteIcon />,
            tooltip: data.isDefault ? "Can't Delete Default Role" : 'Delete',
            onClick: (event, rowData) => {
              handleDelete(rowData.id);
            },
            disabled: data.isDefault
          }),
          {
            icon: () => <RefreshIcon />,
            tooltip: 'Refresh Data',
            onClick: () => {
              refresh();
            },
            isFreeAction: true
          },
          {
            icon: () => <AddIcon />,
            tooltip: 'Create A New Role',
            onClick: () => {
              handleCreationOpen({});
            },
            isFreeAction: true
          }
        ]}
        onChangeRowsPerPage={setPageSize}
        options={{
          actionsColumnIndex: -1,
          search: true,
          pageSizeOptions: Constant.DEFAULT_SEARCH_PAGE_SIZE,
          pageSize
        }}
        icons={{
          Search: () => <SearchIcon color="primary"/>,
        }}
        components={{ OverlayLoading: () => <div /> }}
      />
    </>
  );
};

export default RoleManagementTable;
